<template>
  <div class="third-party-embed">
    <!-- Loading state -->
    <div v-if="isLoading" class="third-party-embed__loading">
      <v-progress-circular indeterminate size="48" width="4" color="primary" />
    </div>

    <!-- The load failed: what it means to a visitor, never the API's sentence -->
    <PlayerRefusal
      v-else-if="embedLoader.failure.value"
      :failure="embedLoader.failure.value"
      :can-retry="false"
    />

    <!-- Provider iframe -->
    <!--
      referrerpolicy: the providers refuse to play when they cannot see which
      site is embedding them (YouTube answers "error 153"). The pages hosting
      this player send `Referrer-Policy: same-origin`, which strips the referrer
      from every cross-origin request, so the element has to state its own.
    -->
    <iframe
      v-else-if="embedUrl"
      :src="embedUrl"
      class="third-party-embed__iframe"
      frameborder="0"
      allowfullscreen
      referrerpolicy="strict-origin-when-cross-origin"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch, toRef } from 'vue'
import type { PropType } from 'vue'
import type { Embed } from '../../types/embed'
import type { ThirdPartyAssetListItem, ThirdPartyPlaybackInfo } from '../../types/asset'
import type { PlayerError } from '../../types/base'
import { useEmbedLoader } from '../../common/composables/useEmbedLoader'
import PlayerRefusal from '../../common/components/PlayerRefusal.vue'
import { usePlaybackInfoCache } from '../../common/composables/usePlaybackInfoCache'

const props = defineProps({
  embedId: {
    type: String,
    default: undefined,
  },
  embed: {
    type: Object as PropType<Embed<ThirdPartyAssetListItem>>,
    default: undefined,
  },
  /** Index of the item to play, for collections holding several items. */
  itemIndex: {
    type: Number,
    default: 0,
  },
  viewerId: {
    type: String,
    default: undefined,
  },
  theme: {
    type: String as PropType<'dark' | 'light'>,
    default: 'dark',
  },
  /** Start playback on load. Providers only honour it together with muting. */
  autoPlay: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  embedLoad: [embed: Embed<ThirdPartyAssetListItem>]
  error: [error: PlayerError]
}>()

const embedIdRef = toRef(props, 'embedId')
const embedPropRef = toRef(props, 'embed')

const embedLoader = useEmbedLoader<ThirdPartyAssetListItem>({
  embedId: embedIdRef,
  embed: embedPropRef,
  autoLoad: true,
})

const playbackInfoCache = usePlaybackInfoCache<ThirdPartyPlaybackInfo>({
  playbackToken: computed(() => embedLoader.embed.value?.playbackToken),
  itemToken: computed(() => embedLoader.embed.value?.itemToken),
})

const isLoading = computed(() => embedLoader.isLoading.value)

const currentItem = computed(() => embedLoader.embed.value?.items?.[props.itemIndex])

// Every provider gates autoplay behind its own muting flag, and they disagree on
// the spelling: YouTube and Dailymotion take `mute`, Vimeo takes `muted`.
const AUTOPLAY_PARAMS: Record<string, Record<string, string>> = {
  youtube: { autoplay: '1', mute: '1' },
  vimeo: { autoplay: '1', muted: '1' },
  dailymotion: { autoplay: '1', mute: '1' },
}

function withAutoplay(url: string, provider: string | undefined): string {
  const params = AUTOPLAY_PARAMS[(provider || '').toLowerCase()]
  if (!params) return url

  // Absolute base: the embed URLs are absolute, and URL() needs one anyway.
  const parsed = new URL(url, window.location.origin)
  // The provider may already carry an explicit choice — leave it alone.
  for (const [key, value] of Object.entries(params)) {
    if (!parsed.searchParams.has(key)) parsed.searchParams.set(key, value)
  }
  return parsed.toString()
}

const embedUrl = computed(() => {
  const asset = currentItem.value?.asset as ThirdPartyAssetListItem | undefined
  if (!asset) return null

  // Try playback info first (more authoritative, creates analytics session)
  const playback = playbackInfoCache.get(asset.uid)
  const url = playback?.embedUrl ?? asset.embedUrl ?? null
  if (!url) return null

  if (!props.autoPlay) return url
  return withAutoplay(url, playback?.provider ?? asset.provider)
})

watch(
  [() => embedLoader.embed.value, currentItem],
  async ([embed, item], previous) => {
    if (!embed) return
    if (embed !== previous?.[0]) emit('embedLoad', embed)
    // Fetch playback info to create analytics session
    if (item) {
      try {
        await playbackInfoCache.fetchOne(item.asset.uid)
      } catch {
        // Non-fatal: we can still display using list item embed URL
      }
    }
  },
  { immediate: true }
)

defineExpose({
  embed: embedLoader.embed,
})
</script>

<style lang="scss" scoped>
.third-party-embed {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  &__loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 32px 16px;
  }
}
</style>
